import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Flex } from '@revenuemarket/cds';

import Table, { FlattenTreeResult, flattenTree } from './Table';
import { TreeData, TreeNodeId } from './utils';

interface Props<T extends Record<string, unknown>> {
  treeData?: TreeData<T>[]; // TreeData에 제네릭 T 전달
  onCellClick?: (result: FlattenTreeResult<T>) => void;
  onCellClear?: () => void;
  foldedAll?: boolean;
  updatedFocusedId?: string;
  filterCondition?: (node: TreeData<T>) => boolean;
  dataHandleCondition?: (node: FlattenTreeResult<T>) => void;
  cellRenderer?: (node: TreeData<T>) => React.ReactNode;
}

const HorizontalTreeTable = <T extends Record<string, unknown>>({
  treeData = [],
  onCellClick,
  onCellClear,
  foldedAll,
  updatedFocusedId,
  filterCondition,
  cellRenderer,
  dataHandleCondition,
}: Props<T>) => {
  const [foldedNodeIds, setFoldedNodeIds] = useState<TreeNodeId[]>([]); // 접혀진 노드 ID 목록

  const flattenedTree = useMemo(() => {
    return flattenTree(treeData);
  }, [treeData]);

  // FOLD 여부가 바뀔때마다 foldedNodeIds와 treeData를 비교하여 fold 여부를 업데이트한다
  const onFoldToggle = (nodeId: TreeNodeId) => {
    const newFoldedNodeIds = foldedNodeIds.includes(nodeId)
      ? foldedNodeIds.filter((id) => id !== nodeId)
      : [...foldedNodeIds, nodeId];
    setFoldedNodeIds(newFoldedNodeIds);
  };

  const onFoldAll = useCallback(
    (foldedAll?: boolean) => {
      if (flattenTree.length === 0) return;
      if (foldedAll) {
        const newFoldedNodeIds = flattenedTree.map((node) => node.key);

        setFoldedNodeIds(newFoldedNodeIds);
      } else {
        setFoldedNodeIds([]);
      }
    },
    [flattenedTree],
  );

  useEffect(() => {
    onFoldAll(foldedAll);
  }, [foldedAll, onFoldAll]);

  return (
    <Flex direction="column" gap={20} tabIndex={0} style={{ outline: 'none' }}>
      <Table
        foldable
        treeData={treeData}
        filterCondition={filterCondition}
        handleCellClick={(result) => {
          onCellClick?.(result);
        }}
        dataHandleCondition={dataHandleCondition}
        cellRenderer={cellRenderer}
        updatedFocusedId={updatedFocusedId}
        onFoldToggle={onFoldToggle}
        onCellClear={onCellClear}
        foldedNodeIds={foldedNodeIds}
        flattenedTree={flattenedTree}
      />
    </Flex>
  );
};

export default HorizontalTreeTable;
